import React, { useState, useEffect, useRef } from 'react';
import '../styles/chat.css';
import '../styles/animations.css';
import { useTheme } from '../hooks/useTheme';
import { themes, Theme } from '../styles/theme';
import { convertMarkdownToHtml } from '../utils/textUtils';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {
  sendChatMessage,
  getConversations,
  getMessages,
  uploadFile,
  getAppParameters,
  getAppInfo,
  getApiKey,
  type ConversationsResponse,
  type MessagesResponse,
  type AppParameters,
  type AppInfo,
} from '../services/difyApi';

interface Message {
  id: string;
  conversation_id: string;
  query: string;
  answer: string;
  created_at: number;
}

interface Conversation {
  id: string;
  name: string;
  created_at: number;
  updated_at: number;
}

const DifyChat: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const currentTheme = themes[theme];
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [currentConversation, setCurrentConversation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [appParameters, setAppParameters] = useState<AppParameters | null>(null);
  const [appInfo, setAppInfo] = useState<AppInfo | null>(null);
  const [textareaHeight, setTextareaHeight] = useState('50px');
  const [currentApiKey, setCurrentApiKey] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const userId = 'test-user'; // In real app, this should come from authentication

  useEffect(() => {
    loadConversations();
    loadAppInfo();
    // 获取当前 API 密钥
    setCurrentApiKey(getApiKey());
  }, []);

  const loadAppInfo = async () => {
    try {
      const parameters = await getAppParameters();
      const info = await getAppInfo();
      setAppParameters(parameters);
      setAppInfo(info);
    } catch (error) {
      console.error('Error loading app info:', error);
    }
  };

  const loadConversations = async () => {
    try {
      setError(null);
      const response: ConversationsResponse = await getConversations(userId);
      setConversations(response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setError('Failed to load conversations');
      setConversations([]);
    }
  };

  const loadConversation = async (conversationId: string) => {
    try {
      setError(null);
      const response: MessagesResponse = await getMessages(conversationId, userId);
      setMessages(response.data);
      setCurrentConversation(conversationId);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Failed to load messages');
      setMessages([]);
    }
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim() || isSending) return;

    const messageText = currentInput;
    setCurrentInput('');
    setIsSending(true);

    // 立即添加用户消息到列表
    const tempMessageId = Date.now().toString();
    const newMessage = {
      id: tempMessageId,
      conversation_id: currentConversation || '',
      query: messageText,
      answer: '',
      created_at: Date.now(),
    };

    setMessages(prev => [...prev, newMessage]);
    scrollToBottom();
    
    try {
      const response = await sendChatMessage({
        query: messageText,
        user: userId,
        conversation_id: currentConversation && !currentConversation.startsWith('temp-') 
          ? currentConversation 
          : undefined,
        response_mode: 'streaming',
        inputs: {}
      });

      // 如果是新会话，更新会话 ID
      if (!currentConversation || currentConversation.startsWith('temp-')) {
        // 处理流式响应以获取会话ID
        const reader = response.getReader();
        const decoder = new TextDecoder();
        let fullAnswer = '';
        let conversationId: string | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          try {
            const lines = chunk.split('\n').filter(line => line.trim());
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                if (jsonStr === '[DONE]') continue;
                
                const data = JSON.parse(jsonStr);
                if (data.conversation_id && !conversationId) {
                  conversationId = data.conversation_id;
                  
                  // 更新当前会话ID
                  setCurrentConversation(conversationId);
                  
                  // 如果是临时会话，更新会话列表
                  if (currentConversation?.startsWith('temp-') && conversationId) {
                    setConversations(prev => prev.map(conv => 
                      conv.id === currentConversation
                        ? { ...conv, id: conversationId as string }
                        : conv
                    ));
                  }
                }
                
                if (data.answer) {
                  fullAnswer += data.answer;
                  
                  // 更新消息列表中的答案
                  setMessages(prev => prev.map(msg => 
                    msg.id === tempMessageId 
                      ? { 
                          ...msg, 
                          answer: fullAnswer, 
                          conversation_id: conversationId ? conversationId : msg.conversation_id 
                        }
                      : msg
                  ));
                }
              }
            }
          } catch (e) {
            console.error('Error parsing streaming data:', e);
          }
          scrollToBottom();
        }
      } else {
        // 处理流式响应
        const reader = response.getReader();
        const decoder = new TextDecoder();
        let fullAnswer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          try {
            const lines = chunk.split('\n').filter(line => line.trim());
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                if (jsonStr === '[DONE]') continue;
                
                const data = JSON.parse(jsonStr);
                if (data.answer) {
                  fullAnswer += data.answer;
                  
                  // 更新消息列表中的答案
                  setMessages(prev => prev.map(msg => 
                    msg.id === tempMessageId 
                      ? { ...msg, answer: fullAnswer }
                      : msg
                  ));
                }
              }
            }
          } catch (e) {
            console.error('Error parsing streaming data:', e);
          }
          scrollToBottom();
        }
      }

      // 流结束后，刷新会话列表
      await loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error instanceof Error ? error.message : 'Failed to send message');
      
      // 如果发生错误，在消息中显示错误状态
      setMessages(prev => prev.map(msg => 
        msg.id === tempMessageId 
          ? { ...msg, answer: '发送失败，请重试' }
          : msg
      ));

      // 如果是临时会话且发生错误，移除该会话
      if (currentConversation?.startsWith('temp-')) {
        setConversations(prev => prev.filter(conv => conv.id !== currentConversation));
        setCurrentConversation(null);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadFile(file, userId);
      console.log('File uploaded:', response.url);
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 创建新会话
  const createNewConversation = () => {
    // 生成临时会话 ID，将在发送第一条消息时被替换为真实 ID
    const tempId = `temp-${Date.now()}`;
    const newConversation = {
      id: tempId,
      name: '新对话',
      created_at: Date.now(),
      updated_at: Date.now(),
    };
    
    // 添加到会话列表
    setConversations(prev => [newConversation, ...prev]);
    
    // 设置为当前会话
    setCurrentConversation(tempId);
    
    // 清空消息列表
    setMessages([]);
    
    // 关闭移动端侧边栏
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
    
    // 聚焦输入框
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // 处理推荐问题点击
  const handleSuggestedQuestionClick = async (question: string) => {
    // 如果正在发送，不处理
    if (isSending) return;
    
    setIsSending(true);
    
    // 始终创建一个新会话
    createNewConversation();
    
    // 等待一下确保新会话已创建
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 获取当前会话ID（应该是刚创建的新会话）
    const conversationId = currentConversation;
    
    // 立即添加用户消息到列表
    const tempMessageId = Date.now().toString();
    const newMessage = {
      id: tempMessageId,
      conversation_id: conversationId || '',
      query: question,
      answer: '',
      created_at: Date.now(),
    };
    
    setMessages(prev => [...prev, newMessage]);
    scrollToBottom();
    
    try {
      const response = await sendChatMessage({
        query: question,
        user: userId,
        conversation_id: conversationId && !conversationId.startsWith('temp-') 
          ? conversationId 
          : undefined,
        response_mode: 'streaming',
        inputs: {}
      });
      
      // 处理响应...与 handleSendMessage 相同的逻辑
      if (!conversationId || conversationId.startsWith('temp-')) {
        // 处理流式响应以获取会话ID
        const reader = response.getReader();
        const decoder = new TextDecoder();
        let fullAnswer = '';
        let newConversationId: string | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          try {
            const lines = chunk.split('\n').filter(line => line.trim());
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                if (jsonStr === '[DONE]') continue;
                
                const data = JSON.parse(jsonStr);
                if (data.conversation_id && !newConversationId) {
                  newConversationId = data.conversation_id;
                  
                  // 更新当前会话ID
                  setCurrentConversation(newConversationId);
                  
                  // 如果是临时会话，更新会话列表
                  if (conversationId?.startsWith('temp-') && newConversationId) {
                    setConversations(prev => prev.map(conv => 
                      conv.id === conversationId
                        ? { ...conv, id: newConversationId as string }
                        : conv
                    ));
                  }
                }
                
                if (data.answer) {
                  fullAnswer += data.answer;
                  
                  // 更新消息列表中的答案
                  setMessages(prev => prev.map(msg => 
                    msg.id === tempMessageId 
                      ? { 
                          ...msg, 
                          answer: fullAnswer, 
                          conversation_id: newConversationId ? newConversationId : msg.conversation_id 
                        }
                      : msg
                  ));
                }
              }
            }
          } catch (e) {
            console.error('Error parsing streaming data:', e);
          }
          scrollToBottom();
        }
      } else {
        // 处理流式响应
        const reader = response.getReader();
        const decoder = new TextDecoder();
        let fullAnswer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          try {
            const lines = chunk.split('\n').filter(line => line.trim());
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                if (jsonStr === '[DONE]') continue;
                
                const data = JSON.parse(jsonStr);
                if (data.answer) {
                  fullAnswer += data.answer;
                  
                  // 更新消息列表中的答案
                  setMessages(prev => prev.map(msg => 
                    msg.id === tempMessageId 
                      ? { ...msg, answer: fullAnswer }
                      : msg
                  ));
                }
              }
            }
          } catch (e) {
            console.error('Error parsing streaming data:', e);
          }
          scrollToBottom();
        }
      }

      // 流结束后，刷新会话列表
      await loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error instanceof Error ? error.message : 'Failed to send message');
      
      // 如果发生错误，在消息中显示错误状态
      setMessages(prev => prev.map(msg => 
        msg.id === tempMessageId 
          ? { ...msg, answer: '发送失败，请重试' }
          : msg
      ));

      // 如果是临时会话且发生错误，移除该会话
      if (conversationId?.startsWith('temp-')) {
        setConversations(prev => prev.filter(conv => conv.id !== conversationId));
        setCurrentConversation(null);
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={`flex h-screen ${currentTheme.background.secondary} theme-transition`}>
      {/* Mobile Menu Button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-lg"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Sidebar */}
      <div className={`
        ${currentTheme.background.primary} border-r ${currentTheme.border.primary} shadow-sm
        md:w-80 w-72 overflow-y-auto custom-scrollbar sidebar-transition
        ${isSidebarOpen ? '' : 'sidebar-hidden'} 
        ${theme === 'dark' ? 'md:relative fixed inset-y-0 left-0 z-40' : ''}
      `}>
        <div className={`sticky top-0 z-10 ${currentTheme.background.primary} border-b ${currentTheme.border.primary} p-4`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${currentTheme.text.primary}`}>
              {appInfo?.name || '智能助手'}
            </h2>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${currentTheme.background.secondary} ${currentTheme.text.primary} hover:${currentTheme.background.tertiary} transition-all duration-200 btn-hover-effect`}
              title={theme === 'light' ? '切换到深色模式' : '切换到浅色模式'}
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>
          </div>
          <button
            onClick={createNewConversation}
            className={`w-full ${currentTheme.accent.gradient} ${currentTheme.accent.primary} text-white px-4 py-3 rounded-xl hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium btn-hover-effect animate-pulse-slow`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            新对话
          </button>
        </div>
        
        {/* Opening Statement Section */}
        {appParameters?.introduction && (
          <div className={`p-4 ${currentTheme.background.secondary} m-3 rounded-lg animate-fadeIn shadow-sm`}>
            <h3 className={`text-md font-medium ${currentTheme.text.primary} mb-2`}>欢迎使用</h3>
            <MarkdownRenderer 
              content={appParameters.introduction}
              className="text-sm whitespace-pre-wrap"
              forceLightMode={true}
            />
          </div>
        )}
        
        {/* Suggested Questions */}
        {appParameters?.suggested_questions && appParameters.suggested_questions.length > 0 && (
          <div className={`p-4 animate-slideIn`}>
            <h3 className={`text-md font-medium ${currentTheme.text.primary} mb-3`}>试着去问</h3>
            <div className="space-y-2">
              {appParameters.suggested_questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestionClick(question)}
                  className={`w-full text-left p-3 rounded-lg ${currentTheme.background.secondary} ${currentTheme.text.secondary} hover:${currentTheme.background.tertiary} transition-colors duration-200 text-sm btn-hover-effect`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* App Description */}
        {/* {appInfo?.description && (
          <div className={`px-4 py-2 animate-fadeIn`} style={{ animationDelay: '0.2s' }}>
            <p className={`${currentTheme.text.secondary} text-sm`}>{appInfo.description}</p>
          </div>
        )}
         */}
        {/* Tags */}
        {appInfo?.tags && appInfo.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 py-2 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
            {appInfo.tags.map((tag, index) => (
              <span 
                key={index} 
                className={`${currentTheme.background.tertiary} ${currentTheme.text.secondary} text-xs px-2 py-1 rounded-md`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Recent Conversations Section */}
        <div className={`mt-4 px-4 py-2 animate-fadeIn`} style={{ animationDelay: '0.4s' }}>
          <h3 className={`text-sm font-medium ${currentTheme.text.primary} mb-2`}>最近对话</h3>
          <div className="space-y-0.5">
            {conversations.slice(0, 5).map((conv, index) => (
              <button
                key={conv.id}
                onClick={() => loadConversation(conv.id)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 animate-slideIn ${
                  currentConversation === conv.id 
                    ? `${currentTheme.background.tertiary} ${currentTheme.text.primary}` 
                    : `hover:${currentTheme.background.secondary} ${currentTheme.text.secondary}`
                }`}
                style={{ animationDelay: `${0.5 + index * 0.1}s` }}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-lg ${currentTheme.accent.gradient} flex items-center justify-center text-white font-medium shadow-sm text-xs`}>
                    {(conv.name?.[0] || '对').toUpperCase()}
                  </div>
                  <div className="truncate text-sm">{conv.name || '新对话'}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col ${currentTheme.background.primary}`}>
        {/* Chat Header */}
        <div className={`py-3 px-4 border-b ${currentTheme.border.primary} ${currentTheme.background.primary} flex justify-between items-center`}>
          <div className="flex items-center">
            <h2 className={`text-lg font-semibold ${currentTheme.text.primary}`}>
              {conversations.find(c => c.id === currentConversation)?.name || '新对话'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`md:hidden p-2 rounded-lg ${currentTheme.background.secondary} ${currentTheme.text.primary} hover:${currentTheme.background.tertiary} btn-hover-effect`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Messages */}
        <div 
          className={`flex-1 overflow-y-auto p-4 ${currentTheme.background.secondary} custom-scrollbar`}
          ref={messagesContainerRef}
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className={`${currentTheme.text.tertiary} text-center max-w-md`}>
                <div className={`${currentTheme.accent.gradient} ${currentTheme.accent.primary} w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-white animate-bounce-slow`}>
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <p className="text-lg font-medium mb-2">开始一个新对话</p>
                <p className="text-sm">
                  {appParameters?.introduction 
                    ? '点击左侧的推荐问题，或在下方输入框中输入您的问题'
                    : '在下方输入框中输入您的问题，开始与AI助手对话'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6 max-w-4xl mx-auto">
              {messages.map((message, index) => (
                <div key={message.id} className="animate-fadeIn" style={{ animationDelay: `${index * 0.1}s` }}>
                  {/* User Message */}
                  <div className="flex flex-col items-end mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs ${currentTheme.text.tertiary}`}>
                        {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                      <div className={`w-6 h-6 rounded-full ${currentTheme.background.tertiary} flex items-center justify-center ${currentTheme.text.primary} text-xs font-medium flex-shrink-0`}>
                        U
                      </div>
                    </div>
                    <div className={`message-bubble max-w-[80%] p-3 rounded-lg ${currentTheme.accent.gradient} text-white shadow-sm`}>
                      {message.query}
                    </div>
                  </div>
                  
                  {/* AI Response */}
                  {(message.answer || isSending) && (
                    <div className="flex flex-col items-start">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-6 h-6 rounded-full ${currentTheme.accent.gradient} flex items-center justify-center text-white text-xs font-medium flex-shrink-0`}>
                          A
                        </div>
                        <span className={`text-xs ${currentTheme.text.tertiary}`}>
                          {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                      <div className={`message-bubble max-w-[80%] p-3 rounded-lg ${currentTheme.background.primary} shadow-sm border ${currentTheme.border.primary}`}>
                        {message.answer ? (
                          <MarkdownRenderer 
                            content={message.answer}
                            className="max-w-none"
                            forceLightMode={true}
                          />
                        ) : (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse typing-dot"></div>
                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse typing-dot"></div>
                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse typing-dot"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        
        {/* Input Area */}
        <div className={`border-t ${currentTheme.border.primary} ${currentTheme.background.primary} p-4 shadow-lg`}>
          <div className="max-w-4xl mx-auto">
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }} className="relative">
              <div className={`flex items-end rounded-xl border ${currentTheme.border.primary} ${currentTheme.background.secondary} overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md`}>
                <textarea
                  ref={inputRef}
                  value={currentInput}
                  onChange={(e) => {
                    setCurrentInput(e.target.value);
                    // 自动调整高度
                    e.target.style.height = 'auto';
                    const newHeight = `${Math.min(e.target.scrollHeight, 200)}px`;
                    setTextareaHeight(newHeight);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="输入消息..."
                  className={`flex-1 p-3 outline-none resize-none ${currentTheme.background.secondary} ${currentTheme.text.primary} min-h-[50px] max-h-[200px]`}
                  style={{ height: textareaHeight }}
                  disabled={isSending}
                />
                <button
                  type="submit"
                  disabled={isSending || !currentInput.trim()}
                  className={`${currentTheme.accent.gradient} ${currentTheme.accent.primary} text-white p-3 m-1 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    isSending || !currentInput.trim() 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg btn-hover-effect'
                  }`}
                >
                  {isSending ? (
                    <div className="flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse typing-dot"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse typing-dot"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse typing-dot"></div>
                    </div>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
              <div className={`mt-2 text-xs ${currentTheme.text.tertiary} px-2 flex items-center justify-between`}>
                <div>
                  {isSending ? '正在发送...' : '按 Enter 发送，Shift + Enter 换行'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={createNewConversation}
                    className={`px-2 py-1 rounded-md hover:${currentTheme.background.tertiary} transition-colors`}
                    title="新建对话"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (inputRef.current) {
                        inputRef.current.focus();
                      }
                    }}
                    className={`px-2 py-1 rounded-md hover:${currentTheme.background.tertiary} transition-colors`}
                    title="聚焦输入框"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DifyChat;
